import React, { useState } from 'react';
import {
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  Icon,
  KeyValue,
  Link,
  Pill,
  SectionHeader,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import AmendedSubjectExamFees from './AmendedSubjectExamFees';
import {
  useGetExamAmendmentDownloadSummary,
  useGetExamAmendmentSummary,
} from '../../../api/candidate/exam/@candidate/queries-services/queries';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import PersonalDetails from '../../examRegistration/components/particulars/PersonalDetails';
import ContactDetails from '../../examRegistration/components/particulars/ContactDetails';
import NextOfKinContactDetails from '../../examRegistration/components/particulars/NextOfKinContactDetails';
import RouteKey from '../../../routes/key';
import FormHeader from '../../../components/form-items/FormHeader';
import { SubjectAmendmentSummaryResponse } from '../../../api/candidate/exam/model';
import { FileUploadState } from '../../../models/File';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';

const useStyles = makeStyles()((theme) =>
({
  downloadPdf: {
    display: 'flex',
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
  paymentContainer: {
    marginBottom: theme.spacing(2),
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      height: 'fit-content',
    },
  },
  tableCaption: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(24),
  },
}));

const { exam, error } = RouteKey.child;
const { examRegistrations } = RouteKey.amendment.child;

const TransactionHistorySubjectAmendment = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const { uuid: applicationUUID } = useParams<any>();
  const history = useHistory();
  const [summary, setSummary] = useState<SubjectAmendmentSummaryResponse>();
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);

  useGetExamAmendmentSummary(
    (res) => setSummary(res),
    (res) => history.push(`/${error}/${res.response?.status}`),
    applicationUUID
  );
  const { refetch, isFetching } = useGetExamAmendmentDownloadSummary(
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    },
    applicationUUID
  );

  const additionalComponentRenderer = (isDownloading = false) => {
    if (isDownloading) {
      return (
        <Icon name={IconName.loadingIndicator} size="L" color={theme.color.link.linkActive02} />
      );
    }
    return (
      <Link className={classes.downloadPdf} id="link-download" onClick={() => refetch()}>
        <Icon name={IconName.download} size="M" color={theme.color.link.linkDefault01} />
        <span className={classes.downloadPdfText}>
          {t('exam->>RegistrationSummary.DownloadSummary')}
        </span>
      </Link>
    );
  };

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  return (
    <>
      {summary && (
        <ContentTemplate type="full">
          <SectionHeader
            title={t('amendment->>ViewDetails.Registration Details')}
          >
            {additionalComponentRenderer(isFetching)}
          </SectionHeader>
          <Grid container direction="row">
            <Grid md={5} sm={12} xs={12}>
              <KeyValue
                id="applicationid"
                keyText={t('amendment->>ViewDetails.Application ID')}
                valueText={summary.applicationStatus.applicationID}
              />
            </Grid>
            <Grid md={5} sm={12} xs={12}>
              <KeyValue
                id="nationality"
                keyText={t('amendment->>ViewDetails.Status')}
                valueText={
                  <Grid>
                    <Pill label={summary.applicationStatus.applicationStatus} variant="positive" />
                  </Grid>
                }
              />
            </Grid>
          </Grid>
          <KeyValue
            id="applicationDateTime"
            keyText={t('amendment->>ViewDetails.Application Date/Time')}
            valueText={stringToLongDateAndShortTime(summary.applicationStatus.applicationDateTime)}
          />
          <PersonalDetails examCandidateDetail={summary.candidateDetails} />
          <ContactDetails contactDetails={summary.candidateDetails} />
          <NextOfKinContactDetails {...summary.candidateDetails} />
          <AmendedSubjectExamFees {...summary} />
          <Divider />
          <Box className={classes.paymentContainer}>
            <FormHeader title={t('amendment->>ViewDetails.Transaction Details')} />
            <KeyValue
              id="cpReferenceNo"
              keyText={t('amendment->>ViewDetails.CP Reference No.')}
              valueText={summary.transactionDetails.internalRef}
            />
            <KeyValue
              id="transactionNo"
              keyText={t('amendment->>ViewDetails.Transaction No.')}
              valueText={summary.transactionDetails.transactionNo}
            />
            <Grid container direction="row">
              <Grid md={5} sm={12} xs={12}>
                <KeyValue
                  id="applicationid"
                  keyText={t('amendment->>ViewDetails.Payment Method')}
                  valueText={summary.payment.paymentMode}
                />
              </Grid>
              <Grid md={5} sm={12} xs={12}>
                <KeyValue
                  id="nationality"
                  keyText={t('amendment->>ViewDetails.Payment Status')}
                  valueText={
                    <Grid>
                      <Pill label={summary.transactionDetails.paymentStatus} variant="positive" />
                    </Grid>
                  }
                />
              </Grid>
            </Grid>
          </Box>
          <Grid xs={12} md={4}>
            <Button
              id="button-selectSubject-back"
              fullWidth
              // className={classes.button}
              variant="outlined"
              onClick={() =>
                history.push(`/${exam}/${examRegistrations.route}`, {
                  from: 'TransactionHistoryPages',
                })
              }
            >
              {t('exam->>RegistrationSummary.BackToTransactionHistory')}
            </Button>
          </Grid>
          {fileStatus === FileUploadState.FAILED && (
            <DownloadProgressModal
              modalState
              fileDownloadStatus={FileUploadState.FAILED}
              onDialogClose={handleCloseDownloadErrorPrompt}
            />
          )}
        </ContentTemplate>
      )}
    </>
  );
};

export default TransactionHistorySubjectAmendment;
